import {
  CreatePracticeTemplateRequest,
  MpxResponseBaseSingle,
  MpxResponseError,
  PracticeTemplate,
  PracticeTemplatesAPI,
  PracticeTemplateSearchQuery,
  UpdatePracticeTemplateRequest,
} from '@cibo/core'
import { useMutation, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { PROGRAMS_QUERY_KEY } from './queryKey'

export const usePracticeTemplates = (
  query: PracticeTemplateSearchQuery,
  options?: Partial<UseQueryOptions<PracticeTemplate[], AxiosError<MpxResponseError>>>
) =>
  useQuery<PracticeTemplate[], AxiosError<MpxResponseError>>({
    queryKey: [PROGRAMS_QUERY_KEY.PRACTICE_TEMPLATES, query.programId, query.taskId, query.owner],
    queryFn: () => PracticeTemplatesAPI.search().then(response => response.items),
    select: templates =>
      templates.filter(
        t =>
          (!query.programId || t.programId === query.programId) &&
          (!query.taskId || t.taskId === query.taskId) &&
          (!query.owner || t.owner === query.owner)
      ),
    ...options,
  })

export const useCreatePracticeTemplate = () => {
  const queryClient = useQueryClient()
  return useMutation<
    MpxResponseBaseSingle<PracticeTemplate>,
    AxiosError<MpxResponseError>,
    CreatePracticeTemplateRequest
  >({
    mutationFn: (request: CreatePracticeTemplateRequest) => PracticeTemplatesAPI.create(request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PROGRAMS_QUERY_KEY.PRACTICE_TEMPLATES],
      })
    },
  })
}

export const useUpdatePracticeTemplate = () => {
  const queryClient = useQueryClient()
  return useMutation<
    MpxResponseBaseSingle<PracticeTemplate>,
    AxiosError<MpxResponseError>,
    UpdatePracticeTemplateRequest
  >({
    mutationFn: (request: UpdatePracticeTemplateRequest) => PracticeTemplatesAPI.update(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROGRAMS_QUERY_KEY.PRACTICE_TEMPLATES] })
    },
  })
}

export const useDeletePracticeTemplate = () => {
  const queryClient = useQueryClient()
  return useMutation<unknown, AxiosError<MpxResponseError>, string>({
    mutationFn: (id: string) => PracticeTemplatesAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROGRAMS_QUERY_KEY.PRACTICE_TEMPLATES] })
    },
  })
}
